import { useState, useMemo } from 'react'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { HiDocument, HiChevronRight, HiFilter } from 'react-icons/hi'
import SEO from '../../components/SEO/SEO'
import { seoBreadcrumbPair } from '../../utils/seoUtils'
import { useLocalizedLink } from '../../hooks/useLocalizedLink'
import './Documents.css'

const DOCUMENT_ITEMS = [
  {
    id: 'internal-charter-2026',
    categoryId: 'statutory',
    titleKey: 'documents.charter2026.title',
    dateKey: 'documents.charter2026.date',
    pagePath: '/documents/charter'
  },
  {
    id: 'attestation-guidelines-2026',
    categoryId: 'certification',
    titleKey: 'documents.attestationGuidelines2026.title',
    dateKey: 'documents.attestationGuidelines2026.date',
    pagePath: '/documents/attestation-guidelines'
  }
]

function Documents() {
  const { t } = useTranslation()
  const getLocalizedLink = useLocalizedLink()
  const [selectedCategory, setSelectedCategory] = useState('all')

  const breadcrumbItems = useMemo(
    () => seoBreadcrumbPair(t('common.home'), t('common.documents'), 'documents'),
    [t]
  )

  const categories = [
    { id: 'all', name: t('documents.all', 'All Documents') },
    { id: 'statutory', name: t('documents.statutory', 'Statutory') },
    { id: 'regulatory', name: t('documents.regulatory', 'Regulatory') },
    { id: 'certification', name: t('documents.certification', 'Certification') },
    { id: 'forms', name: t('documents.forms', 'Forms') },
    { id: 'protocols', name: t('documents.protocols', 'Protocols') },
    { id: 'international', name: t('documents.international', 'International') },
    { id: 'methodological', name: t('documents.methodological', 'Methodological') }
  ]

  const documents = useMemo(() => {
    return DOCUMENT_ITEMS.filter(
      (doc) => selectedCategory === 'all' || doc.categoryId === selectedCategory
    )
  }, [selectedCategory])

  return (
    <div className="documents-page">
      <SEO 
        title={t('common.documents')}
        description={t('documents.description', 'Official documents of SUMA International')}
        breadcrumbItems={breadcrumbItems}
      />
      <div className="container">
        <div className="documents-hero">
          <h1>{t('common.documents')}</h1>
          <p className="documents-intro">
            {t('documents.intro', 'Access official documents, forms, protocols, and other materials from SUMA International.')}
          </p>
        </div>

        <div className="documents-filters">
          <HiFilter className="filter-icon" />
          <select
            value={selectedCategory}
            onChange={(e) => setSelectedCategory(e.target.value)}
            className="category-select"
          >
            {categories.map(category => (
              <option key={category.id} value={category.id}>
                {category.name}
              </option>
            ))}
          </select>
        </div>

        <div className="documents-list">
          {documents.length > 0 ? (
            documents.map(doc => (
              <div key={doc.id} className="document-card">
                <HiDocument className="document-icon" />
                <div className="document-info">
                  <h3>{t(doc.titleKey)}</h3>
                  <p className="document-category">{t(`documents.${doc.categoryId}`, doc.categoryId)}</p>
                  <p className="document-date">{t(doc.dateKey)}</p>
                </div>
                <Link
                  to={getLocalizedLink(doc.pagePath)}
                  className="btn btn-secondary download-btn"
                  aria-label={`${t('documents.openPage', 'Open')} — ${t(doc.titleKey)}`}
                  title={t('documents.openPage', 'Open')}
                >
                  <HiChevronRight className="btn-icon" aria-hidden />
                </Link>
              </div>
            ))
          ) : (
            <div className="no-documents">
              <p>{t('documents.noDocuments', 'Documents will be displayed here as they are added to the system.')}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default Documents
